import { ExclamationCircleOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Col, Popover, Row, Skeleton, Space } from 'antd';
import { eq } from 'lodash';
import { useNavigate } from 'react-router';
import { FilterBarConstant, NavigatePathConstant } from 'src/constants';
import { ClientServicesLink, CurrencyField, MTooltip, MTypography } from 'src/lib';
import { BalanceSummaryView, TransferStatusLabel } from 'src/models';
import { Color, Display, FlexDirection, Spacing } from 'src/styles';

import * as Styles from './SummaryInfo.styles';

const titleRowStyle = css`
  width: 100%;
`;

const balanceSummaryFieldRowStyle = css`
  width: 100%;
  border-radius: 2px;
  ${Spacing.p12}
`;

const summaryInfoRowStyle = css`
  width: 100%;
  ${Display.flex}
  ${FlexDirection.column}
  ${Spacing.ml16}
`;

interface BalanceSummaryFieldProps {
  label: string;
  value?: number;
  color?: string;
  onClick?: () => void;
  hasError: boolean;
  error?: string;
  isLoading: boolean;
  alwaysShow?: boolean;
  tooltip?: {
    title?: string;
    content?: string;
  };
}

const BalanceSummaryField = ({
  label,
  value,
  color,
  hasError,
  error,
  isLoading = true,
  alwaysShow = false,
  onClick,
  tooltip,
}: BalanceSummaryFieldProps) => {
  if (eq(value, 0) && !hasError && !alwaysShow) {
    return null;
  }

  return (
    <Row align='middle' justify='space-between' className={balanceSummaryFieldRowStyle}>
      <Col>
        <MTypography
          variant='h6'
          weight='semibold'
          color={color}
          testId={`summary-${label.toLowerCase().replaceAll(' ', '-')}-label`}>
          {label}
          {tooltip && (
            <>
              {' '}
              <MTooltip type='popover' title={tooltip.title}>
                {tooltip.content}
              </MTooltip>
            </>
          )}
        </MTypography>
      </Col>
      <Col>
        {hasError ? (
          <Popover
            placement='topRight'
            content={
              <MTypography variant='body1' color={Color.Red7}>
                {error ?? (
                  <>
                    An unexpected error occurred. Please try again or contact <ClientServicesLink /> for assistance.
                  </>
                )}
              </MTypography>
            }
            overlayClassName={Styles.popoverWrapper}
            overlayInnerStyle={{ borderRadius: '2px', padding: '12px 16px 13px 16px' }}>
            <ExclamationCircleOutlined style={{ fontSize: '24px', color: Color.Red7 }} />
          </Popover>
        ) : isLoading ? (
          <Skeleton.Input active size='small' />
        ) : (
          <MTypography variant='h6' weight='semibold' color={color}>
            <Row align='middle'>
              <Space size={16} direction='horizontal'>
                <CurrencyField value={value} testId={`summary-${label.toLowerCase().replaceAll(' ', '-')}-value`} />
                {onClick && <RightOutlined onClick={onClick} style={{ color: Color.Gray7 }} />}
              </Space>
            </Row>
          </MTypography>
        )}
      </Col>
    </Row>
  );
};

interface BalanceSummaryProps {
  summary: Omit<BalanceSummaryView, 'cashBalance' | 'accountValue'>;
  onRefresh: () => void;
}

export const BalanceSummary = ({ summary, onRefresh }: BalanceSummaryProps) => {
  const navigate = useNavigate();

  return (
    <Row className={summaryInfoRowStyle}>
      <Row justify='space-between' align='middle' wrap={false} className={cx(titleRowStyle, Spacing.mx4, Spacing.mb16)}>
        <Col flex='auto' data-testid={'summary-account-details'}>
          <MTypography variant='h5' weight='bold' color={Color.Gray7}>
            Account Details
          </MTypography>
        </Col>
        <Col flex='none' className={Styles.refresh} onClick={onRefresh}>
          <Space size={4}>
            <ReloadOutlined />
            <span>Refresh</span>
          </Space>
        </Col>
      </Row>
      <Row>
        <Space direction='vertical' size={16} className={titleRowStyle}>
          <BalanceSummaryField
            label='Cash Available'
            value={summary.cashAvailable.value}
            hasError={summary.cashAvailable.hasError}
            error={summary.cashAvailable?.error}
            isLoading={summary.cashAvailable.isLoading}
            color={Color.Green7}
            alwaysShow
          />
          <BalanceSummaryField
            label='Pending Money Transactions'
            value={summary.pendingTransfersTotal.value}
            hasError={summary.pendingTransfersTotal.hasError}
            error={summary.pendingTransfersTotal.error}
            isLoading={summary.pendingTransfersTotal.isLoading}
            onClick={() =>
              navigate(
                `../${NavigatePathConstant.HISTORY}?show=${JSON.stringify([
                  FilterBarConstant.Show.DepositTransfer,
                  FilterBarConstant.Show.WithdrawalTransfer,
                ])}&filterBy=${JSON.stringify([
                  `${TransferStatusLabel.PENDING}_${FilterBarConstant.StatusCategory.MoneyTransfer}`,
                  `${TransferStatusLabel.PROCESSING}_${FilterBarConstant.StatusCategory.MoneyTransfer}`,
                ])}`,
              )
            }
            color={Color.Orange7}
          />
          <BalanceSummaryField
            label='Pending Offer Orders'
            value={summary.pendingOfferOrdersTotal.value}
            hasError={summary.pendingOfferOrdersTotal.hasError}
            error={summary.pendingOfferOrdersTotal?.error}
            isLoading={summary.pendingOfferOrdersTotal.isLoading}
            color={Color.Red7}
            alwaysShow
          />
          <BalanceSummaryField
            label='Working Securities Orders'
            value={summary.workingOrdersTotal.value}
            hasError={summary.workingOrdersTotal.hasError}
            error={summary.workingOrdersTotal?.error}
            isLoading={summary.workingOrdersTotal.isLoading}
            color={Color.Red9}
            tooltip={{
              content: 'Includes only working security buy orders, as these reduce your cash available',
            }}
            alwaysShow
          />
          <BalanceSummaryField
            label='Securities Holdings'
            value={summary.tradedPositionsTotal.value}
            hasError={summary.tradedPositionsTotal.hasError}
            error={summary.tradedPositionsTotal.error}
            isLoading={summary.tradedPositionsTotal.isLoading}
            color={Color.Brand7}
            alwaysShow
          />
          <BalanceSummaryField
            label='Non-Traded Holdings'
            value={summary.nonTradedPositionsTotal.value}
            hasError={summary.nonTradedPositionsTotal.hasError}
            error={summary.nonTradedPositionsTotal?.error}
            isLoading={summary.nonTradedPositionsTotal.isLoading}
            color={Color.Brand9}
            alwaysShow
          />
        </Space>
      </Row>
    </Row>
  );
};
